import React, { useState, useContext, useEffect } from "react";
import {
  ScrollView,
  StyleSheet,
  View,
} from "react-native";

import { ProductContext } from "../contexts/product/Store";
import { AuthContext } from "../contexts/auth/Store";
import UpdateStoreLocation from "../components/profile/UpdateStoreLocation";
import HomeScreenProductListing from "../components/product/HomeScreenProductListing";
import { IBuyer } from "../interfaces/buyer";
import GenericHeader from "../components/headers/GenericHeader";
import { useNavigation } from "@react-navigation/core";
import SearchForm from "../components/search/SearchForm";
import EmptyView from "../components/common/EmptyView";
import http from "../utils/http";
import { IProduct } from "../interfaces/product";
import { GetPricetag } from "../constants/Utils";
import { debounce, unionBy } from "lodash";
import { AnalyticsService } from "../constants/Analytics";
import Fuse from "fuse.js";
import ProductStack from "../components/product/ProductStack";
import useMounted from "../hooks/useMounted";
import ProductStackHeader from "../components/product/ProductStackHeader";

const analytics = new AnalyticsService();

interface ISearch {
  exact: IProduct[];
  match: IProduct[];
  result: IProduct[];
}

export default function ProductExploreScreen() {
  const navigation = useNavigation();
  const isMounted = useMounted();
  const auth: any = useContext(AuthContext);
  const token = auth.getUserData.userToken;
  const buyer: IBuyer = auth.getUserData.buyerData;
  const { product_state } = useContext(ProductContext);
  const debounceExtendedSearch = React.useMemo(
    () => debounce(doExtendedSearch, 800),
    []
  );

  const [queryString, setQueryString] = useState("");
  const [isLoading, setLoading] = useState<boolean>(false);
  const [search, setSearch] = useState<ISearch>({
    exact: [],
    match: [],
    result: [],
  });

  const clearQueryString = () => {
    setQueryString("");
  };

  const getStickyIndices = (): number[] => {
    let indices: number[] = [];
    if (search.match.length) {
      if (search.exact.length) {
        indices = [0, 2];
      } else {
        indices = [1];
      }
    }
    return indices;
  };

  function doExtendedSearch(query: string, existing_list: IProduct[]) {
    let form = new FormData();
    form.append("query", query);
    form.append("filter", "");
    form.append("location_id", buyer?.location_id || '');
    http
      .post("product/search_available", form, token)
      .then((res) => {
        let merged_list: any[] = [];
        if (res.code === 200) {
          let server_list = res.data.filter((product: IProduct) => {
            let product_pricetag = GetPricetag(product);
            if (product_pricetag && product.status === "active") {
              product["pricetag"] = product_pricetag;
              return product;
            }
            return false;
          });
          merged_list = unionBy(server_list, existing_list, "_id");
        } else {
          console.log(res);
          analytics.logErrorMessage(
            res?.message || "Error while searching from server"
          );
        }

        isMounted && dispatchSearchResult(query, merged_list);
        isMounted && setLoading(false);
      })
      .catch((error) => {
        console.log(error);
        analytics.logError(error);
        isMounted && setLoading(false);
      });
  }

  function dispatchSearchResult(query: string, list: IProduct[]) {
    const options = {
      includeScore: true,
      ignoreLocation: true,
      keys: ["name", "description", "brand.name", "manufacturer.name"],
      threshold: 0.4,
      distance: 1000,
    };

    const fuse = new Fuse(list, options);
    const result = fuse.search(query).map((x: any) => {
      x.item["score"] = x.score;
      return x.item;
    });

    isMounted &&
      setSearch((prev: ISearch) => {
        return {
          ...prev,
          exact: result.filter((x) => x.score < 0.02),
          match: result.filter((x) => x.score >= 0.02),
          result: result,
        };
      });
  }

  useEffect(() => {
    if (queryString !== "") {
      setLoading(true);
      // run search
      // set loading to false when search is done
      debounceExtendedSearch(queryString, []);
    } else {
      // set loading to false
      setLoading(false);
    }
    return () => {};
  }, [queryString]);

  return (
    <View style={styles.container}>
      {buyer && buyer.location && buyer.location.name !== "Other*" ? (
        <>
          <GenericHeader
            title={
              product_state?.search_context
                ? `Search ${product_state?.search_context?.model_data?.name} products`
                : "Buy Products"
            }
            backAction={() => {
              navigation.navigate("HomeTab", { screen: "Category" });
            }}
          />

          <SearchForm
            isLoading={isLoading}
            queryString={queryString}
            setQueryString={setQueryString}
            clearQueryString={clearQueryString}
          />

          {queryString !== "" ? (
            <ScrollView stickyHeaderIndices={getStickyIndices()}>
              {search.exact.length ? (
                <View>
                  <ProductStackHeader
                    title={`Found ${search.exact.length} product${
                      search.exact.length > 0 ? "s" : ""
                    }`}
                    isLoading={isLoading}
                  />
                </View>
              ) : null}
              {search.exact.length ? (
                <View>
                  <ProductStack
                    products={search.exact}
                    isLoading={isLoading}
                    showHeading={false}
                  />
                </View>
              ) : isLoading ? (
                <ProductStack
                  products={[]}
                  isLoading={true}
                  showHeading={false}
                />
              ) : (
                <EmptyView
                  summary={`No product found matching ${queryString}`}
                />
              )}
              {search.match.length ? (
                <ProductStackHeader
                  title={`Similar to '${queryString}'`}
                  isLoading={false}
                />
              ) : null}
              {search.match.length ? (
                <View>
                  <ProductStack
                    products={search.match}
                    isLoading={isLoading}
                    showHeading={false}
                  />
                </View>
              ) : null}
            </ScrollView>
          ) : (
            <HomeScreenProductListing />
          )}
        </>
      ) : (
        <UpdateStoreLocation />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexGrow: 1,
  },
  listItem: {
    height: 60,
    color: "white",
    backgroundColor: "grey",
    borderBottomColor: "white",
    borderBottomWidth: 2,
    borderStyle: "solid",
  },
});
